import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import * as R from 'ramda'
import { getQuestionTitle } from '../../../utils/QuestionHelper'
import QuestionElement from './QuestionElement' // change for newer QuestionItemList
import Button from '../../../UniformComponents/Button'
import Modal from '../../../UniformComponents/Modal'

import './styles/mandatoryPopup.scss'

// stages: 0 = init; 1 = has missing mandatory questions; 2 = has mising SG option
@observer
class MandatoryPopup extends Component {
    constructor(props) {
        super(props)
        this.state = {
            stage: undefined,
            smartGridQuestions: [],
            mandatoryQuestions: {},
            hasInheritedQuestions: false,
            loading: true,
            dontLoad: false,
            dismissed: false,
            questionsAdded: false,
            initialised: false,
        }
        const { store } = props
        if (!store.survey.mandatoryPopupShown) {
            this.init()
            store.survey.mandatoryPopupShown = true
        } else {
            this.init()
        }

        const { stage } = this.state
        if (stage === 0) this.setState({ dontLoad: true })
    }

    init = () => {
        let stage = 0
        const smartGridQuestions = this.getSmartQuestions()
        if (smartGridQuestions && smartGridQuestions.length) stage = 2

        const missingMandatory = this.getMissingMandatory()
        if (Object.keys(missingMandatory).length) stage = 1

        this.setState({
            stage,
            smartGridQuestions,
            mandatoryQuestions: missingMandatory,
            loading: false,
            initialised: true,
        })
    }

    getSmartQuestions = () => {
        const { store } = this.props
        const { survey } = store

        const questions = survey.pages
            .map((p) => (p.questions ? p.questions.map((q) => ({ ...q, page: p })) : undefined))
            .filter((q) => q !== undefined && q.length).slice().flat()

        const smartGridQuestions = questions
            .filter((question) => question.question !== undefined && question.question.questionTypeCode === 'SG'
                && (!R.pathOr([], ['optionSetY', 'options'], question).length))

        return smartGridQuestions
    }

    editQuestion = (question) => {
        const {
            store,
        } = this.props
        const { page } = question

        store.questionDetails = {
            action: 'editQuestion',
            prevQuestions: page.prev_questions,
            parentId: page.prev_questions.find((inherit) => inherit.value === question.parentId),
            oid: question.oid,
            pageQuestionId: question.id,
            survey: store.survey,
            tokenData: store.tokenData,
            questionId: question.questionId,
            questionIndex: question.priority - 1,
            usedInOtherSurveys: question.usedInOtherSurveys,
            usedInParentSurvey: question.usedInParentSurvey,
            pageIndex: page.priority - 1,
            smartGrid: question.question.questionTypeCode === 'SG' ? question.optionSetY : undefined,
        }
        store.showQuestionEditor = true
        this.close()
    }

    getMissingMandatory = () => {
        const { store } = this.props
        const { survey } = store
        const { questionLibrary } = survey
        const { questionLibraryStore } = questionLibrary

        const missingMandatory = {}
        const surveyQuestionIds = new Set()
        survey.pages.forEach((p) => {
            p.questions?.forEach((q) => {
                surveyQuestionIds.add(q.questionId.toLowerCase())
            })
        })

        const allMandatory = questionLibraryStore.questionLibraryLists.get('mandatory')

        allMandatory.forEach((mq) => {
            const questionFound = surveyQuestionIds.has(mq.questionId.toLowerCase())
            if (!questionFound) {
                missingMandatory[mq.objectname] = missingMandatory[mq.objectname] || []
                missingMandatory[mq.objectname].push(mq)
            }
        })

        return missingMandatory
    }

    getObjectNameText = (objectName) => {
        const { store } = this.props
        const { userData, survey } = store
        const { metadata } = survey
        switch (objectName) {
            case 'Client':
                return userData.clientName
            case 'Survey':
                return survey.content.get(store.langcode).text
            case 'Product':
                return metadata.productInfo.productName
            case 'Portfolio':
                return survey.portfolioName
            default:
                return 'other'
        }
    }

    getTitleByLocale = ({ question }) => {
        const { store } = this.props
        const locale = store.surveyLocale
        const { defaultLocale } = store.survey.metadata
        const { langcode } = store
        const titleMap = question.content.shortTitle

        let title = ''

        if (titleMap.has(locale)) {
            title = titleMap.get(locale).text
        } else if (titleMap.has(defaultLocale)) {
            title = titleMap.get(defaultLocale).text
        } else if (titleMap.has(langcode)) {
            title = titleMap.get(langcode).text
        }
        return title
    }

    getQuestionTitleByLocale = (question) => {
        const { store } = this.props
        const { surveyLocale } = store.surveyLocale
        const { initialLocale } = question
        const { defaultLocale } = store.survey.metadata
        const titleObj = question.locales

        return R.pathOr(question.title, ['text'], getQuestionTitle({
            content: titleObj, locale: surveyLocale, defaultLocale, initialLocale,
        }))
    }

    close = (smartGridQuestions) => {
        const { stage } = this.state
        if (stage === 1 && smartGridQuestions.length) {
            return this.setState({ stage: 2, questionsAdded: false, smartGridQuestions })
        }
        return this.setState({ dismissed: true })
    }

    renderCard = (type, questions) => (
        Object.keys(questions).map((category) => (
            <div className="missingQuestionsCard">
                <div className="header">
                    <p>
                        {`${type} questions set across all `}
                        <span>{`${this.getObjectNameText(category)} surveys`}</span>
                    </p>
                </div>
                <div className="questionList">
                    {this.renderQuestions(questions[category])}
                </div>
            </div>
        ))
    )

    renderQuestions = (questions) => {
        const { stage } = this.state
        const { store } = this.props

        return questions.map((q) => (
            <QuestionElement
                title={stage !== 2 ? this.getQuestionTitleByLocale(q) : this.getTitleByLocale(q)}
                question={q}
                store={store}
                stage={stage}
                editQuestion={() => this.editQuestion(q)}
            />
        ))
    }

    addQuestion = async (question, survey) => {
        const { store } = this.props
        if (question.parentId) {
            this.setState({ hasInheritedQuestions: true })
            return
        }
        question.oid = question.questionoid
        await survey.pages[0].addQuestion({ question, survey, store })
    }

    addMandatoryQuestions = async () => {
        const { store } = this.props
        const { survey } = store
        const { mandatoryQuestions } = this.state

        for (const category of Object.keys(mandatoryQuestions)) {
            for (const mq of mandatoryQuestions[category]) {
                await this.addQuestion(mq, survey)
            }
        }

        this.setState({ questionsAdded: true, loading: false })
    }

    renderButtons = () => {
        const {
            stage, mandatoryQuestions, smartGridQuestions, loading, questionsAdded,
        } = this.state

        if (questionsAdded) {
            return <Button onClick={() => this.close(smartGridQuestions)} cssClass="primary" label={i18n.t('ok')} />
        }

        const closeButton = (
            <Button
                onClick={() => this.close(smartGridQuestions)}
                cssClass="tertiary"
                disabled={loading}
                label={i18n.t('surveyBuilder.modal.mandatory.btn1')}
            />
        )

        if (stage === 2) return closeButton

        const mandatoryQExists = Object.keys(mandatoryQuestions).length
        const addMandatoryButton = mandatoryQExists
            ? (
                <Button
                    onClick={() => this.addMandatoryQuestions()}
                    cssClass="primary"
                    disabled={loading}
                    label={i18n.t('surveyBuilder.modal.mandatory.btn2')}
                />
            ) : null

        return (
            <Fragment>
                {closeButton}
                {addMandatoryButton}
            </Fragment>
        )
    }

    getModalTitles = () => {
        const { stage, questionsAdded, hasInheritedQuestions } = this.state
        let modalSubTitle = ''
        let modalTitle = ''

        if (!questionsAdded) {
            const titleBase = 'surveyBuilder.modal.mandatory.missing_'
            const subTitleBase = 'mandatoryPopup_explanation_'
            modalTitle = stage === 2
                ? i18n.t(`${titleBase}sg_options`)
                : i18n.t(`${titleBase}mandatory`)
            modalSubTitle = stage === 2
                ? i18n.t(`${subTitleBase}sg_options`)
                : i18n.t(`${subTitleBase}mandatory`)
        } else if (hasInheritedQuestions) {
            modalTitle = i18n.t('mandatoryPopup_title_containsInheritedQuestion')
            modalSubTitle = i18n.t('mandatoryPopup_explanation_containsInheritedQuestion')
        } else {
            modalTitle = i18n.t('mandatoryPopup_title_questionsAdded')
            modalSubTitle = i18n.t('mandatoryPopup_explanation_questionsAdded')
        }
        return { modalTitle, modalSubTitle }
    }

    getShowModal = () => {
        const {
            mandatoryQuestions,
            smartGridQuestions,
            dismissed,
        } = this.state

        const hasMandatoryMissing = Object.keys(mandatoryQuestions).length
        const hasSGOptionsMissing = smartGridQuestions.length
        return dismissed ? false : (hasMandatoryMissing || hasSGOptionsMissing)
    }

    render() {
        const {
            stage,
            mandatoryQuestions,
            smartGridQuestions,
            dontLoad,
            questionsAdded,
            initialised,
        } = this.state
        const { isOtherModalOpened } = this.props
        if (dontLoad || isOtherModalOpened) return null
        if (!initialised) this.init()

        const hasMandatoryMissing = Object.keys(mandatoryQuestions).length !== 0
        const { modalTitle, modalSubTitle } = this.getModalTitles()
        const showModal = this.getShowModal()

        return (
            <Modal
                show={showModal}
                onClose={() => this.close(smartGridQuestions)}
                cssClass="large"
                containsScroll
            >
                <div id="mandatoryPopup" className="no-scroll-wrapper">
                    <div className="uc-modal__header">
                        <h1>{modalTitle}</h1>
                        {modalSubTitle
                            && <p className="uc-modal__desc sm" dangerouslySetInnerHTML={{ __html: modalSubTitle }} />}
                    </div>

                    {!questionsAdded && (
                        <div className="uc-modal__scrollable-content">
                            {stage === 1 && hasMandatoryMissing && this.renderCard('Mandatory', mandatoryQuestions)}
                            {stage === 2 ? this.renderQuestions(smartGridQuestions) : null}
                        </div>
                    )}

                    <div className="uc-modal__button-panel large right">
                        {this.renderButtons()}
                    </div>
                </div>
            </Modal>
        )
    }
}

export default MandatoryPopup
